import { useState } from "react";
import { api, apiErr } from "../lib/api";
import { useAppData } from "../context/AppDataContext";
import { toast } from "sonner";
import { Megaphone, Send, Loader2, MessageCircle, Phone, Bell, X } from "lucide-react";

const inp = "w-full border border-line bg-white rounded-md px-3 py-2 text-sm outline-none focus:border-clinical";

const CHANNELS = [
  { key: "inapp", label: "In-app + push", icon: Bell, hint: "Always delivered" },
  { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, hint: "Needs Twilio keys" },
  { key: "sms", label: "SMS", icon: Phone, hint: "Needs Twilio keys" },
];

// Bulk broadcast to registered patients. System/Super Admin only.
export function AdminBroadcast() {
  const { clinics } = useAppData();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [audience, setAudience] = useState("all");
  const [channels, setChannels] = useState({ inapp: true, whatsapp: false, sms: false });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const toggle = (k) => setChannels((c) => ({ ...c, [k]: !c[k] }));
  const selected = Object.keys(channels).filter((k) => channels[k]);

  const reset = () => { setMessage(""); setAudience("all"); setChannels({ inapp: true, whatsapp: false, sms: false }); setResult(null); };

  const send = async () => {
    if (!message.trim()) return toast.error("Enter a message to broadcast.");
    if (selected.length === 0) return toast.error("Select at least one channel.");
    setSending(true);
    try {
      const { data } = await api.post("/broadcasts", {
        message: message.trim(),
        clinic_id: audience === "all" ? null : audience,
        channels: selected,
      });
      setResult(data);
      toast.success(`Broadcast sent to ${data.recipients} patient(s).`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSending(false); }
  };

  return (
    <>
      <button
        onClick={() => { reset(); setOpen(true); }}
        data-testid="broadcast-open-btn"
        className="inline-flex items-center gap-1.5 border border-clinical/40 text-clinical px-4 py-2 rounded-md text-sm font-semibold hover:bg-clinical/5 transition-colors"
      >
        <Megaphone className="w-4 h-4" /> Broadcast to patients
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="broadcast-modal" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-1">
              <h3 className="font-heading font-bold text-xl text-ink flex items-center gap-2"><Megaphone className="w-5 h-5 text-clinical" /> Broadcast to patients</h3>
              <button onClick={() => setOpen(false)} data-testid="broadcast-close-btn" className="text-muted2 hover:text-ink"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-muted2 mb-4">Send a one-way message to registered patients. WhatsApp & SMS require Twilio keys; in-app + push always works.</p>

            {result ? (
              <div data-testid="broadcast-result" className="space-y-3">
                <div className="bg-calm/10 border border-calm/30 rounded-lg p-4">
                  <p className="font-heading font-bold text-ink">Broadcast delivered</p>
                  <p className="text-sm text-muted2 mt-1">Reached {result.recipients} patient(s).</p>
                  <ul className="text-sm text-ink mt-3 grid gap-1">
                    <li>• In-app + push: <b>{result.inapp}</b></li>
                    <li>• WhatsApp sent: <b>{result.whatsapp_sent}</b></li>
                    <li>• SMS sent: <b>{result.sms_sent}</b></li>
                    {result.no_phone > 0 && <li className="text-muted2">• No phone on file (WhatsApp/SMS skipped): {result.no_phone}</li>}
                  </ul>
                  {(result.whatsapp_sent === 0 && result.sms_sent === 0 && (selected.includes("whatsapp") || selected.includes("sms"))) && (
                    <p className="text-xs text-alert mt-3">Note: WhatsApp/SMS sent 0 — this is expected until Twilio keys are configured in the backend.</p>
                  )}
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm font-semibold text-muted2 hover:text-ink">Close</button>
                  <button onClick={reset} data-testid="broadcast-new-btn" className="inline-flex items-center gap-1.5 bg-clinical text-white rounded-md px-4 py-2 text-sm font-semibold">Send another</button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-muted2">Message</label>
                  <textarea
                    data-testid="broadcast-message"
                    rows={4}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="e.g. Free blood-pressure screening this Saturday at Mahusekwa District Hospital, 9am–1pm. All welcome."
                    className={`${inp} mt-1 resize-none`}
                    maxLength={2000}
                  />
                  <p className="text-xs text-muted2 mt-1">{message.length}/2000</p>
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-muted2">Audience</label>
                  <select data-testid="broadcast-audience" value={audience} onChange={(e) => setAudience(e.target.value)} className={`${inp} mt-1`}>
                    <option value="all">All registered patients</option>
                    {clinics.filter((c) => c.active).map((c) => (
                      <option key={c.id} value={c.id}>Patients of {c.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-muted2 mt-1">Clinic audiences also include patients who follow all clinics.</p>
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-muted2">Channels</label>
                  <div className="grid sm:grid-cols-3 gap-2 mt-1">
                    {CHANNELS.map((ch) => {
                      const on = channels[ch.key];
                      return (
                        <button
                          key={ch.key}
                          type="button"
                          onClick={() => toggle(ch.key)}
                          data-testid={`broadcast-channel-${ch.key}`}
                          className={`flex flex-col items-start gap-0.5 rounded-md border px-3 py-2 text-left transition-colors ${on ? "border-clinical bg-clinical/5" : "border-line hover:border-clinical/40"}`}
                        >
                          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink"><ch.icon className="w-4 h-4 text-clinical" /> {ch.label}</span>
                          <span className="text-[11px] text-muted2">{ch.hint}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-1">
                  <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm font-semibold text-muted2 hover:text-ink">Cancel</button>
                  <button
                    onClick={send}
                    disabled={sending}
                    data-testid="broadcast-send-btn"
                    className="inline-flex items-center gap-1.5 bg-clinical text-white rounded-md px-5 py-2 text-sm font-semibold hover:bg-clinicaldark disabled:opacity-60"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send broadcast
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
